/* @jsx jsx */
import { useCallback, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';

import type { VideoPlayer } from 'bundles/item-lecture';
import { NOT_SET, playbackRates } from 'bundles/video-player/constants';
import type { PlaybackRate, VideoPlayerEventingV3UIFunction } from 'bundles/video-player/types/VideoPlayer';
import userPreferences from 'bundles/video-player/utils/userPreferences';

export const usePlaybackRateControls = (
  player: VideoPlayer,
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction
) => {
  const [currentPlaybackRate, setCurrentPlaybackRate] = useState<PlaybackRate | null>(player.playbackRate());

  const onPlaybackRateChange = useCallback<() => $TSFixMe>(() => {
    const currPlaybackRate = player ? player.playbackRate() : 1;
    setCurrentPlaybackRate(currPlaybackRate);
    userPreferences.set('playbackRate', currPlaybackRate);
  }, [player]);

  useEffect(() => {
    player.on('ratechange', onPlaybackRateChange);

    return () => {
      player.off('ratechange', onPlaybackRateChange);
    };
  }, [onPlaybackRateChange, player]);

  const onIncreasePlaybackRateClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const currentPlaybackRateIndex = playbackRates.findIndex((rate) => rate === currentPlaybackRate);
    const newPlaybackRateIndex = Math.min(playbackRates.length - 1, currentPlaybackRateIndex + 1);
    const newPlaybackRate = playbackRates[newPlaybackRateIndex];

    if (trackVideoPlayerClickEvent) {
      trackVideoPlayerClickEvent('video_playback_rate_increase', {
        previousPlaybackRate: Math.round(currentPlaybackRate ?? NOT_SET),
        playbackRate: newPlaybackRate,
      });
    }
    player.playbackRate(newPlaybackRate);
  };

  const onDecreasePlaybackRateClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const currentPlaybackRateIndex = playbackRates.findIndex((rate) => rate === currentPlaybackRate);
    const newPlaybackRateIndex = Math.max(0, currentPlaybackRateIndex - 1);
    const newPlaybackRate = playbackRates[newPlaybackRateIndex];
    if (trackVideoPlayerClickEvent) {
      trackVideoPlayerClickEvent('video_playback_rate_decrease', {
        previousPlaybackRate: Math.round(currentPlaybackRate ?? NOT_SET),
        playbackRate: newPlaybackRate,
      });
    }
    player.playbackRate(newPlaybackRate);
  };

  const onPlaybackRateClick = (playbackRate: PlaybackRate) => {
    const newPlaybackRateIndex = playbackRates.findIndex((rate) => rate === playbackRate);
    const newPlaybackRate = playbackRates[newPlaybackRateIndex];

    player.playbackRate(newPlaybackRate);

    // TODO (https://coursera.atlassian.net/browse/LRNRSCCSS-3231): add tracking for playback rate change
  };

  return {
    onIncreasePlaybackRateClick,
    onDecreasePlaybackRateClick,
    currentPlaybackRate,
    supportedPlaybackRates: playbackRates as PlaybackRate[],
    onPlaybackRateClick,
  };
};
